/**
 * Paper preview section for the StatelessWorkCardsUpsellModal.
 *
 * Renders the preview column: loading spinner, paper list with
 * optional fade footer, empty-filter state, and the desktop
 * "Customize PDF Pack" link when filters are collapsed.
 */

import React from 'react';

import { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import { WorkId } from 'js/liswp/types';
import { Body as BodyText } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { reportPreviewImpression } from 'js/bulk_download/bulk_download_timing';
import {
  PaperPreviewList,
  FadeFooter,
} from 'js/bulk_download/work_cards_shared/PaperPreviewList';

import {
  PreviewColumn,
  DesktopCustomizeLink,
  DesktopCustomizeIconWrap,
  PreviewContainer,
  ValueBanner,
  ValueBannerTitleWrap,
  PaperContentArea,
  PreviewContentFlex,
  LoadingState,
  Spinner,
  EmptyFilterState,
  EmptyFilterIcon,
  primaryTextStyle,
  emptyFilterBodyStyle,
  valueBannerTextStyle,
} from './StatelessWorkCardsUpsellModal.styles';

const FULL_WIDTH_PREVIEW_COLUMN_STYLE: React.CSSProperties = {
  gridColumn: '1 / -1',
};

export interface ModalPreviewSectionProps {
  loading: boolean;
  hideFilters: boolean;
  filtersExpanded: boolean;
  isMobile: boolean;
  badgePlacement: 'side' | 'below' | 'above';
  onExpandFilters: () => void;
  sourceWorkId: WorkId;
  sourceWorkPreview?: BundleWork;
  modalVariant: string;
  isPremium: boolean;
  showAnnotations: boolean;
  visibleRelatedWorks: BundleWork[];
  totalRelatedCount: number;
  showFadeFooter: boolean;
  hiddenPapersCount: number;
  hasActiveFilters: boolean;
  /**
   * Impression source for the preview list.
   *
   * ImpressionWatcher de-dupes by (source, entity_type, entity_id) so
   * Premium and non-Premium MUST use different sources.
   */
  previewImpressionSource: string;
  onPreviewImpression: () => void;
  showValueBanner: boolean;
  publisherSiteValue: number;
  totalPaperCount: number;
  averagePricePerPaper: number;
}

export function ModalPreviewSection({
  loading,
  hideFilters,
  filtersExpanded,
  isMobile,
  badgePlacement,
  onExpandFilters,
  sourceWorkId,
  sourceWorkPreview,
  modalVariant,
  isPremium,
  showAnnotations,
  visibleRelatedWorks,
  totalRelatedCount,
  showFadeFooter,
  hiddenPapersCount,
  hasActiveFilters,
  previewImpressionSource,
  onPreviewImpression,
  showValueBanner,
  publisherSiteValue,
  totalPaperCount,
  averagePricePerPaper,
}: ModalPreviewSectionProps) {
  return (
    <PreviewColumn
      style={
        hideFilters || (!filtersExpanded && !isMobile)
          ? FULL_WIDTH_PREVIEW_COLUMN_STYLE
          : undefined
      }
    >
      {!hideFilters && !filtersExpanded && (
        <DesktopCustomizeLink type="button" onClick={onExpandFilters}>
          <DesktopCustomizeIconWrap aria-hidden>
            <Icon name="left_panel_close" size={18} />
          </DesktopCustomizeIconWrap>
          <BodyText size="md" bold as="span">
            Customize PDF pack
          </BodyText>
        </DesktopCustomizeLink>
      )}
      <PreviewContainer>
        {loading ? (
          <LoadingState>
            <Spinner aria-hidden />
            <BodyText size="md" as="div" style={primaryTextStyle}>
              Preparing your PDF pack...
            </BodyText>
          </LoadingState>
        ) : (
          <>
            {showValueBanner && (
              <ValueBanner>
                <ValueBannerTitleWrap>
                  <BodyText size="xl" as="p" style={valueBannerTextStyle}>
                    ${publisherSiteValue.toLocaleString('en-US')} on publisher
                    sites. Download {totalPaperCount}{' '}
                    {totalPaperCount === 1 ? 'paper' : 'papers'} for $1
                  </BodyText>
                </ValueBannerTitleWrap>
                <BodyText size="sm" as="p" style={valueBannerTextStyle}>
                  Avg. ${averagePricePerPaper} per paper on top publisher sites
                </BodyText>
              </ValueBanner>
            )}
            <PaperContentArea>
              <ImpressionWatcher
                as={PreviewContentFlex}
                source={previewImpressionSource}
                entity_type={ImpressionEntity.Work}
                entity_id={sourceWorkId}
                extra={{
                  modalVariant,
                  is_premium: isPremium,
                  bundleSize: totalRelatedCount,
                  previewWorks: visibleRelatedWorks.slice(0, 10).map((w) => ({
                    id: w.id,
                    b: w.bucket,
                    bi: w.bucketIndex,
                    ss: w.similarityScore,
                  })),
                }}
                onImpression={() => {
                  reportPreviewImpression(sourceWorkId);
                  onPreviewImpression();
                }}
              >
                <PaperPreviewList
                  works={visibleRelatedWorks}
                  includedWork={sourceWorkPreview}
                  showAnnotations={showAnnotations}
                  badgePlacement={badgePlacement}
                  maxHeight="100%"
                  footer={
                    showFadeFooter ? (
                      <FadeFooter hiddenPapersCount={hiddenPapersCount} />
                    ) : undefined
                  }
                  style={{ flex: 1, minHeight: 0 }}
                  emptyState={
                    hasActiveFilters ? (
                      <EmptyFilterState>
                        <EmptyFilterIcon aria-hidden>
                          <Icon name="search" size={32} />
                        </EmptyFilterIcon>
                        <BodyText
                          size="lg"
                          bold
                          as="div"
                          style={primaryTextStyle}
                        >
                          No matching papers found
                        </BodyText>
                        <BodyText
                          size="sm"
                          as="div"
                          style={emptyFilterBodyStyle}
                        >
                          Try adjusting your filters or search terms to discover
                          more related research.
                        </BodyText>
                      </EmptyFilterState>
                    ) : undefined
                  }
                />
              </ImpressionWatcher>
            </PaperContentArea>
          </>
        )}
      </PreviewContainer>
    </PreviewColumn>
  );
}

export default ModalPreviewSection;
